import classNames from 'classnames';
import { Formik } from 'formik';
import clone from 'just-clone';
import { useState } from 'react';
import { PhoneType, DMSType } from '../../../../API/Enums';
import { IAppointment } from '../../../../API/GoMoto';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import {
    useAppointmentState,
    useAppointmentDispatch,
    useAppointmentUpdateAndPersist,
    useMaskPhone,
    useMaskEmail,
    ContactPreferenceEnum,
    AppointmentDispatchTypes,
} from '../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocalization, useLocalizationExtension } from '../../../../context/Localization/localization';
import { LocalizationType } from '../../../../context/Localization/localization.types';
import { CTAButton, GMContactPreferenceButton } from '../../../../controls/ButtonBase';
import { GMForm, GMFormWrapper, GMErrorMessage } from '../../../../controls/Form';
import { GMGrid } from '../../../../controls/Grid';
import { Heading } from '../../../../controls/Heading/Heading';
import { KeyboardLayouts } from '../../../../controls/SimpleKeyboard/Layout';
import { Spacer } from '../../../../controls/Spacer/Spacer';
import { GMFormTextField, GMFormPhoneField, GMFormEmailField } from '../../../../controls/TextField';
import { nameof } from '../../../../helpers/nameof';
import { TowInDetailsForm, TowInDetailsProps } from './TowInDetails.types';
import * as Yup from 'yup';
import css from './TowInDetails.module.scss';

/**
 * Contact preference component for tow-in appointments. This component exists separately
 * because it does not contain any logic to handle opting in or out of texting and diverges
 * heavily from the `CustomerDetails` component.
 *
 * @param onProceed - {@link TowInDetailsProps.onProceed onProceed} description.
 */
const TowInDetails: GoMotoComponent<TowInDetailsProps> = ({
    onProceed,
}) => {
    // Localization Hooks
    const Localization: LocalizationType = useLocalization();
    const { emailInvalid } = useLocalizationExtension();

    // Kiosk Hooks
    const {
        dealer: {
            customerTextingEnabled,
            key: dealerKey,
            dmsType,
        },
    } = useKioskState();

    // Appointment Hooks
    const appointment: IAppointment = useAppointmentState();
    const appointmentDispatch = useAppointmentDispatch();
    const setAppointment = useAppointmentUpdateAndPersist();
    const maskPhone: boolean = useMaskPhone();
    const maskEmail: boolean = useMaskEmail();

    // Variables
    const [inputedPhone, setInputedPhone] = useState<boolean>(false);
    const [inputedEmail, setInputedEmail] = useState<boolean>(false);

    // Formik Initialization
    const initialValues: TowInDetailsForm = {
        phone: appointment.phone,
        email: appointment.email,
        phoneTypeEnum: appointment.phoneTypeEnum ?? PhoneType.cell,
        lastName: appointment.lastName,
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object()
                .shape({
                    [nameof<TowInDetailsForm>('email')]: Yup.string()
                        .trim()
                        .email(emailInvalid)
                        .nullable(),
                    [nameof<TowInDetailsForm>('phone')]: Yup.string()
                        .trim()
                        .nullable(),
                    [nameof<TowInDetailsForm>('contactPreference')]: Yup.string()
                        .nullable(),
                })}
            onSubmit={async (values: TowInDetailsForm) => {
                const originalAppointment: IAppointment = clone(appointment);

                const isNewCustomer: boolean = !!appointment.dmsCustomerKey;
                const wasEmailUpdated: boolean = initialValues.email.toLowerCase() !== values.email.toLowerCase() || isNewCustomer;
                const wasPhoneUpdated: boolean = initialValues.phone !== values.phone || isNewCustomer;
                const wasPhoneEnumUpdated: boolean = initialValues.phoneTypeEnum !== values.phoneTypeEnum;
                const wasContactPreferenceUpdated: boolean = originalAppointment.contactPreference !== values.contactPreference;
                const wasCustomerInfoUpdated: boolean = appointment.customerInfoUpdated
                    || wasEmailUpdated
                    || wasPhoneUpdated
                    || wasPhoneEnumUpdated
                    || wasContactPreferenceUpdated
                    || dmsType === DMSType.CDKEDGE;

                if (inputedPhone) {
                    appointmentDispatch({
                        type: AppointmentDispatchTypes.UnmaskField,
                        payload: 'phone',
                    });
                }
                if (inputedEmail) {
                    appointmentDispatch({
                        type: AppointmentDispatchTypes.UnmaskField,
                        payload: 'email',
                    });
                }

                const updatedAppointment: IAppointment = {
                    ...appointment,
                    ...values,
                    email: values.email.trim(),
                    dealerKey,
                    emailUpdated: wasEmailUpdated,
                    phoneUpdated: wasPhoneUpdated,
                    customerInfoUpdated: wasCustomerInfoUpdated,
                    ...(wasEmailUpdated && {
                        oldEmail: initialValues.email.toLowerCase(),
                    }),
                    ...(wasPhoneUpdated && {
                        oldPhone: initialValues.phone,
                    }),
                    ...(wasPhoneEnumUpdated && {
                        oldPhoneTypeEnum: initialValues.phoneTypeEnum,
                    }),
                };
                return setAppointment(updatedAppointment)
                    .then(onProceed);
            }}
        >
            {({ errors, submitCount }) => {
                return (
                    <GMForm
                        heading={
                            <Heading
                                primaryHeading={Localization.CustomerContact}
                                secondaryHeading={Localization.LetsGatherSomeCustomerContactInfo}
                                leftAlign
                            />
                        }
                        submitButton={
                            <CTAButton
                                styleType={'primary'}
                                type={'submit'}
                                title={Localization.ClickToProceedToNextStep}
                                label={Localization.Proceed}
                                id={Localization.ClickToProceedToNextStep}
                                ariaLabel={Localization.ClickToProceedToNextStep}
                            />
                        }
                        topDivider
                    >
                        <GMGrid id={'tow-in-details-phone-email-list-container'} container>
                            <GMGrid id={'tow-in-details-required-fields-grid'} xs={12}>
                                <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                            </GMGrid>
                            <GMGrid id={'tow-in-details-customer-name-grid'} xs={12}>
                                <GMFormTextField
                                    name={nameof<typeof initialValues>('lastName')}
                                    id={nameof<typeof initialValues>('lastName')}
                                    title={Localization.CustomerName}
                                    label={Localization.CustomerName}
                                    ariaLabel={Localization.CustomerName}
                                    keyboardLayout={KeyboardLayouts.AlphanumericShift}
                                    autoFocus={initialValues.lastName === ''}
                                    shiftAfterSpace
                                    shiftAfterPeriod
                                    shiftAfterAmpersand
                                    required
                                />
                            </GMGrid>
                            <GMGrid id={'tow-in-details-phone-grid'} xs={12}>
                                <GMFormPhoneField
                                    name={nameof<typeof initialValues>('phone')}
                                    id={nameof<typeof initialValues>('phone')}
                                    title={Localization.PhoneNumber}
                                    label={Localization.PhoneNumber}
                                    ariaLabel={Localization.PhoneNumber}
                                    initiallyMasked={maskPhone}
                                    onChangeCallback={() => setInputedPhone(true)}
                                    autoFocus={initialValues.lastName !== '' && initialValues.phone === ''}
                                    required
                                />
                            </GMGrid>
                            <GMGrid id={'tow-in-details-email-grid'} xs={12}>
                                <GMFormEmailField
                                    name={nameof<typeof initialValues>('email')}
                                    id={nameof<typeof initialValues>('email')}
                                    title={Localization.EmailAddress}
                                    label={Localization.EmailAddress}
                                    ariaLabel={Localization.EmailAddress}
                                    initiallyMasked={maskEmail}
                                    onChangeCallback={() => setInputedEmail(true)}
                                    autoFocus={initialValues.phone !== '' && initialValues.email === ''}
                                    keyboardLayout={KeyboardLayouts.Email}
                                />
                            </GMGrid>
                        </GMGrid>
                        <Spacer />
                        <Heading secondaryHeading={Localization.HowShouldWeContactTheCustomer} />
                        <GMGrid
                            id={'tow-in-details-contact-button-form-container'}
                            container
                            rowGap={1}
                            className={css.contactButtonList}
                        >
                            <GMGrid
                                id={'tow-in-details-contact-button-container'}
                                container
                                xs={12}
                                gap={3}
                                className={classNames(css.contactButtons, customerTextingEnabled ? css.threeButtons : css.twoButtons)}
                            >
                                <GMGrid id={'tow-in-details-email-button-grid'} xs={customerTextingEnabled ? 4 : 6}>
                                    <GMContactPreferenceButton
                                        name={nameof<typeof initialValues>('contactPreference')}
                                        contactPreference={ContactPreferenceEnum.Email}
                                        buttonSizeClassName={css.contactButton}
                                    />
                                </GMGrid>
                                <GMGrid id={'tow-in-details-phone-button-grid'} xs={customerTextingEnabled ? 4 : 6}>
                                    <GMContactPreferenceButton
                                        name={nameof<typeof initialValues>('contactPreference')}
                                        contactPreference={ContactPreferenceEnum.Phone}
                                        buttonSizeClassName={css.contactButton}
                                    />
                                </GMGrid>
                                {customerTextingEnabled ? (
                                    <GMGrid id={'tow-in-details-text-button-grid'} xs={4}>
                                        <GMContactPreferenceButton
                                            name={nameof<typeof initialValues>('contactPreference')}
                                            contactPreference={ContactPreferenceEnum.Text}
                                            buttonSizeClassName={css.contactButton}
                                        />
                                    </GMGrid>
                                ) : null}
                            </GMGrid>
                            {submitCount !== 0 && errors.contactPreference && (
                                <GMGrid
                                    id={'tow-in-details-contact-button-error-container'}
                                    container
                                    xs={12}
                                    className={css.error}
                                >
                                    <GMGrid id={'tow-in-details-contact-button-error-grid'} xs={12}>
                                        <GMErrorMessage error>
                                            {errors.contactPreference}
                                        </GMErrorMessage>
                                    </GMGrid>
                                </GMGrid>
                            )}
                        </GMGrid>
                    </GMForm>
                );
            }}
        </Formik >
    );
};

export default TowInDetails;
